import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
  Fade,
} from "@mui/material";
import { login } from "../actions/userActions";
import { trackEvent } from "../analytics";
import AccountStatusDialog from "../dialogs/AccountStatusDialog";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { ModernTextField, ModernButton, GradientBackground, AuthCard } from "../components/UI/ModernComponents";

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastText, setToastText] = useState("");
  const [isLoading, setisLoading] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [notVerifiederrorOpen, setNotVerifiedErrorOpen] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const loginToastShown = useRef(false);
  const loginAttemptedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo, error } = userLogin;
  const userInfoRef = useRef(userInfo);
  userInfoRef.current = userInfo;

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (email.trim() === "") {
      setUsernameError("Email is required");
      return false;
    } else if (!emailRegex.test(email)) {
      setUsernameError("Please enter a valid email address");
      return false;
    } else {
      setUsernameError("");
      return true;
    }
  };

  const validatePassword = (pass) => {
    if (pass.trim() === "") {
      setPasswordError("Password is required");
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setUsername(newEmail);
    validateEmail(newEmail);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const handleEmailBlur = () => {
    validateEmail(username);
  };

  const handlePasswordBlur = () => {
    validatePassword(password);
  };

  const handleErrorClose = () => {
    setErrorOpen(false);
    setNotVerifiedErrorOpen(false);
  };

  const handleClose = () => {
    setOpenToast(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const isEmailValid = validateEmail(username);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      trackEvent("Form", "Login form validation failed", "Login form validation failed");
      return;
    }
    setisLoading(true);
    loginAttemptedRef.current = true;
    dispatch(login(username, password));
    trackEvent("Form", "Login Attempt", "Login Attempt");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  useEffect(() => {
    if (userInfo?.status === "success" || userInfo) {
      if (loginAttemptedRef.current && !loginToastShown.current) {
        loginToastShown.current = true;
        trackEvent("Form", "Successful Login", `user_id: ${userInfo._id}`);
        toast.success("Login successful!", {
          autoClose: 500,
        });
      }
      setisLoading(false);
      const redirectTo = location.state?.from
        ? `${location.state.from.pathname}${location.state.from.search || ""}`
        : "/app/home";
      navigate(redirectTo, { replace: true });
      return;
    }

    if (error) {
      setisLoading(false);

      if (error.includes("User is not active")) {
        setErrorOpen(true);
        return;
      }

      if (error.includes("User is not verified")) {
        setNotVerifiedErrorOpen(true);
        return;
      }

      setToastText(error);
      setOpenToast(true);
      trackEvent("Login Form", "Login Failure", "User: Invalid credentials");
    }
  }, [userInfo, error, navigate, dispatch, location.state]);

  const handleRegisterClick = () => {
    navigate("/signup");
  };

  // Clear any existing error when component mounts, but never wipe an
  // already-authenticated session (e.g. persisted login rehydrating when
  // the home address is opened in a new tab).
  useEffect(() => {
    if (!userInfoRef.current) {
      dispatch({ type: "USER_LOGIN_RESET" });
    }
  }, [dispatch]);

  if (userInfo) {
    // Already authenticated (e.g. persisted session) — avoid flashing the
    // login form while the redirect effect navigates to the home page.
    return null;
  }

  return (
    <>
      <Helmet>
        <title>LawGPT - Login</title>
      </Helmet>
      <GradientBackground>
        <Fade in timeout={800}>
          <AuthCard elevation={0}>
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              {/* Logo */}
              <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                <img
                  src="/images/legalSearchLogo2.png"
                  alt="LawGPT Logo"
                  style={{
                    width: "180px",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "24px", sm: "28px" },
                  color: "#12243C",
                  mb: 1,
                  fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                  background: "linear-gradient(135deg, #12243C 0%, #0073A6 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Welcome back
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#6B7280",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                }}
              >
                Sign in to your LawGPT account
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
              <ModernTextField
                fullWidth
                label="Email address"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
                    </InputAdornment>
                  ),
                }}
                value={username}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                onKeyDown={handleKeyPress}
                error={!!usernameError}
                helperText={usernameError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: "12px",
                    marginLeft: "4px",
                    color: "#EF4444",
                    fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                  },
                }}
              />

              <ModernTextField
                fullWidth
                label="Password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ 
                          color: "#9CA3AF",
                          padding: "4px",
                          "&:hover": {
                            color: "#6B7280",
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={password}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                onKeyDown={handleKeyPress}
                error={!!passwordError}
                helperText={passwordError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: "12px",
                    marginLeft: "4px",
                    color: "#EF4444",
                    fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                  },
                }}
              />

              {/* Forgot Password */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Typography
                  onClick={() => navigate("/forgot-password")}
                  sx={{
                    cursor: "pointer",
                    color: "#0073A6",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                    "&:hover": {
                      color: "#005A8A",
                      textDecoration: "underline",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Forgot password?
                </Typography>
              </Box>

              {/* Login Button */}
              <ModernButton
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{
                  backgroundColor: "#072035",
                  "&:hover": {
                    backgroundColor: "#005A8A",
                  },
                  "&:disabled": {
                    backgroundColor: "#E5E7EB",
                    color: "#9CA3AF",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
                ) : (
                  "Sign in"
                )}
              </ModernButton>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" sx={{ color: "#9CA3AF", px: 2, fontSize: "13px", fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important" }}>
                or
              </Typography>
            </Divider>

            {/* Register Link */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#6B7280",
                  fontSize: "13px",
                  fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                }}
              >
                Don't have an account?{" "}
                <Typography
                  component="span"
                  onClick={handleRegisterClick}
                  sx={{
                    cursor: "pointer",
                    color: "#0073A6",
                    fontWeight: 600,
                    fontSize: "13px",
                    fontFamily: "Inter, Inter Fallback, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
                    "&:hover": {
                      color: "#00C4F0",
                      textDecoration: "underline",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Create account
                </Typography>
              </Typography>
            </Box>
          </AuthCard>
        </Fade>

        {/* Error Toast */}
        {(error || openToast) && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={openToast}
            autoHideDuration={4000}
            onClose={handleClose}
          >
            <Alert
              sx={{ width: "100%" }}
              severity="error"
              variant="filled"
              onClose={handleClose}
            >
              {toastText}
            </Alert>
          </Snackbar>
        )}

        <AccountStatusDialog
          open={errorOpen}
          onClose={handleErrorClose}
          title="Account Inactive"
          description="This account is inactive. Our team will notify you via email once your account has been activated."
        />

        <AccountStatusDialog
          open={notVerifiederrorOpen}
          onClose={handleErrorClose}
          title="Account Not Verified"
          description="This account is not verified. Please verify your email."
        />
      </GradientBackground>
    </>
  );
}

export default LoginPage;
